/**
 * Waste Module UI Store - Zustand
 *
 * @module core/modules/waste/store
 *
 * @description
 * Gestiona el estado de la UI específico del módulo Waste.
 * NO gestiona modos operacionales - eso lo hace el ModeSelector global.
 *
 * @remarks
 * Este store solo maneja:
 * - Selección de elementos waste (contenedor, ruta, zona, depósito)
 * - Estado de conexión en tiempo real (GPS tracking)
 * - Filtros específicos del módulo
 * - Vista del panel derecho waste
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { eventBus } from '../../../event-bus';
import type {
  VehicleType,
  ContainerType,
  WasteRightView,
} from '../types';

/* ===== TYPES ===== */

/**
 * Tipo de selección actual en el módulo waste
 */
export type WasteSelectionType = 'none' | 'vehicle' | 'container' | 'route' | 'zone' | 'depot' | 'trip';

/**
 * Datos de selección
 */
export interface WasteSelection {
  type: WasteSelectionType;
  id: string | null;
  data: unknown | null;
}

/**
 * Estado del store
 */
export interface WasteUIState {
  /**
   * Selección actual
   */
  selection: WasteSelection;

  /**
   * Vista del panel derecho
   * @default 'summary'
   */
  rightPanelView: WasteRightView;

  /* ===== FILTERS ===== */

  vehicleTypeFilter: VehicleType[];
  containerTypeFilter: ContainerType[];
  statusFilter: string[];

  /* ===== REALTIME ===== */

  /**
   * Estado de conexión WebSocket para GPS
   */
  isConnected: boolean;

  /**
   * Timestamp de la última actualización de posición
   */
  lastPositionUpdate: number | null;
}

/**
 * Acciones del store
 */
export interface WasteUIActions {
  /**
   * Seleccionar elemento
   */
  selectElement: (type: WasteSelectionType, id: string, data?: unknown) => void;

  /**
   * Limpiar selección
   */
  clearSelection: () => void;

  /**
   * Cambiar vista del panel derecho
   */
  setRightPanelView: (view: WasteRightView) => void;

  /* ===== FILTERS ===== */

  setVehicleTypeFilter: (types: VehicleType[]) => void;
  setContainerTypeFilter: (types: ContainerType[]) => void;
  setStatusFilter: (statuses: string[]) => void;
  clearFilters: () => void;

  /* ===== REALTIME ===== */

  setConnected: (connected: boolean) => void;
  setLastPositionUpdate: (timestamp: number) => void;

  /**
   * Resetear estado a inicial
   */
  reset: () => void;
}

/**
 * Store completo (estado + acciones)
 */
export type WasteUIStore = WasteUIState & WasteUIActions;

/* ===== INITIAL STATE ===== */

const initialState: WasteUIState = {
  selection: {
    type: 'none',
    id: null,
    data: null,
  },
  rightPanelView: 'summary',

  // Filters
  vehicleTypeFilter: [],
  containerTypeFilter: [],
  statusFilter: [],

  // Realtime
  isConnected: false,
  lastPositionUpdate: null,
};

/* ===== STORE ===== */

/**
 * Waste UI Store con Zustand
 *
 * @remarks
 * Gestiona el estado específico del módulo Waste.
 * Los modos operacionales (visualize, edit, etc.) son gestionados
 * por el store global del ModeSelector.
 */
export const useWasteUIStore = create<WasteUIStore>()(
  devtools(
    (set, get) => ({
      // Estado inicial
      ...initialState,

      selectElement: (type: WasteSelectionType, id: string, data?: unknown): void => {
        // Mapear tipo a vista del panel derecho
        const viewMap: Record<WasteSelectionType, WasteRightView> = {
          none: 'summary',
          vehicle: 'vehicleDetails',
          container: 'containerDetails',
          route: 'routeDetails',
          zone: 'zoneDetails',
          depot: 'depotDetails',
          trip: 'tripMonitor',
        };

        set({
          selection: { type, id, data },
          rightPanelView: viewMap[type],
        });

        eventBus.emit('waste.selection.changed', {
          type,
          id,
          timestamp: Date.now(),
        });

        console.debug(`[WasteUI] Selected ${type}: ${id}`);
      },

      clearSelection: (): void => {
        const previousSelection = get().selection;

        if (previousSelection.type === 'none') return;

        set({
          selection: { type: 'none', id: null, data: null },
          rightPanelView: 'summary',
        });

        eventBus.emit('waste.selection.changed', {
          type: 'none',
          timestamp: Date.now(),
        });

        console.debug('[WasteUI] Selection cleared');
      },

      setRightPanelView: (view: WasteRightView): void => {
        const previousView = get().rightPanelView;

        if (previousView === view) return;

        set({ rightPanelView: view });

        eventBus.emit('waste.view.changed', {
          view,
          previousView,
          timestamp: Date.now(),
        });

        console.debug(`[WasteUI] Panel view changed: ${previousView} → ${view}`);
      },

      // Filters
      setVehicleTypeFilter: (types) => set({ vehicleTypeFilter: types }),
      setContainerTypeFilter: (types) => set({ containerTypeFilter: types }),
      setStatusFilter: (statuses) => set({ statusFilter: statuses }),
      clearFilters: () =>
        set({
          vehicleTypeFilter: [],
          containerTypeFilter: [],
          statusFilter: [],
        }),

      // Realtime
      setConnected: (connected) => set({ isConnected: connected }),
      setLastPositionUpdate: (timestamp) => set({ lastPositionUpdate: timestamp }),

      reset: (): void => {
        set(initialState);
        console.debug('[WasteUI] Reset to initial state');
      },
    }),
    {
      name: 'waste-ui-store',
    }
  )
);

/* ===== SELECTORS ===== */

export const selectSelection = (state: WasteUIStore): WasteSelection => state.selection;
export const selectHasSelection = (state: WasteUIStore): boolean => state.selection.type !== 'none';
export const selectRightPanelView = (state: WasteUIStore): WasteRightView => state.rightPanelView;
export const selectIsConnected = (state: WasteUIStore): boolean => state.isConnected;
